/**
 * Settings → Security → Trusted Devices
 *
 * Every browser the user signs in from is recorded as a trusted device
 * (most-recently-used first). "Remember me" at sign-in keeps that
 * device signed in for 30 days instead of 7. Revoking a device is
 * enforced — any session token minted on it is rejected on its next
 * request. Revoking the current device signs this browser out too.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  ShieldCheck, Smartphone, Trash2, Monitor, Globe2, Clock,
  AlertTriangle, Loader2, KeyRound, ExternalLink,
} from 'lucide-react';
import api from '@/lib/api';

/* eslint-disable -- react-hooks/set-state-in-effect false-positive on the initial fetch effect. */

function deviceIcon(ua) {
  const u = (ua || '').toLowerCase();
  if (u.includes('iphone') || u.includes('ipad') || u.includes('android')) {
    return Smartphone;
  }
  return Monitor;
}

function relative(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString();
}

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  const days = Math.ceil((d.getTime() - Date.now()) / (86400 * 1000));
  return days;
}

export default function TrustedDevices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [revokingAll, setRevokingAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/auth/trusted-devices');
      setDevices(data?.devices || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load trusted devices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const revoke = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/auth/trusted-devices/${id}`);
      toast.success('Device revoked');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    } finally {
      setBusyId(null);
    }
  };

  const revokeAll = async () => {
    if (!confirm('Revoke ALL trusted devices? Every session — including this one — will be signed out.')) return;
    setRevokingAll(true);
    try {
      const { data } = await api.delete('/auth/trusted-devices');
      toast.success(`Revoked ${data?.revoked || 0} device${data?.revoked === 1 ? '' : 's'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke all failed');
    } finally {
      setRevokingAll(false);
    }
  };

  const activeDevices = devices.filter((d) => !d.revoked_at);
  const revokedDevices = devices.filter((d) => d.revoked_at);

  return (
    <main
      data-testid="trusted-devices-page"
      className="mx-auto max-w-3xl px-4 sm:px-6 py-8 sm:py-12 space-y-8"
    >
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#8B3FC5]">
          <ShieldCheck size={12} />
          <span>Security</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Trusted Devices</h1>
        <p className="text-sm text-slate-600 dark:text-white/60 max-w-2xl">
          Every browser you sign in from is recorded here as a trusted device. Signing in with &ldquo;Remember me&rdquo; keeps that device signed in for 30 days. Revoke any device below — it is signed out on its very next request.
        </p>
      </header>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-slate-500 dark:text-white/50">
          {loading ? 'Loading…' : `${activeDevices.length} active · ${revokedDevices.length} revoked`}
        </div>
        <button
          type="button"
          onClick={revokeAll}
          disabled={revokingAll || activeDevices.length === 0}
          data-testid="trusted-devices-revoke-all"
          className="inline-flex items-center gap-1.5 rounded-md border border-rose-300 dark:border-rose-700/60 bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 text-xs font-semibold px-3 py-1.5 transition-colors hover:bg-rose-100 dark:hover:bg-rose-500/15 disabled:opacity-50"
        >
          {revokingAll ? <Loader2 size={12} className="animate-spin" /> : <AlertTriangle size={12} />}
          Sign out of every device
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 dark:text-white/50 text-sm">
          <Loader2 size={14} className="animate-spin" /> Loading devices…
        </div>
      ) : activeDevices.length === 0 && revokedDevices.length === 0 ? (
        <div
          data-testid="trusted-devices-empty"
          className="rounded-lg border border-dashed border-slate-300 dark:border-white/15 p-8 text-center"
        >
          <KeyRound size={24} className="mx-auto text-slate-400 dark:text-white/40" />
          <h3 className="mt-3 text-sm font-semibold text-slate-700 dark:text-white/80">No trusted devices yet</h3>
          <p className="mt-1 text-xs text-slate-500 dark:text-white/50 max-w-md mx-auto">
            Sign in from any browser and it will appear here automatically. Use &ldquo;Remember me&rdquo; at sign-in to keep a device signed in for 30 days.
          </p>
        </div>
      ) : (
        <>
          {/* Active devices */}
          {activeDevices.length > 0 && (
            <section className="space-y-3" data-testid="trusted-devices-active-list">
              <h2 className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-white/45 font-bold">
                Active ({activeDevices.length})
              </h2>
              <ul className="space-y-2">
                {activeDevices.map((d) => {
                  const Icon = deviceIcon(d.ua);
                  const expiresIn = daysUntil(d.expires_at);
                  return (
                    <li
                      key={d.id}
                      data-testid={`trusted-device-row-${d.id}`}
                      className={`flex items-start gap-3 rounded-lg border p-4 ${
                        d.is_current
                          ? 'border-[#8B3FC5]/40 bg-[#8B3FC5]/[0.06]'
                          : 'border-slate-200 dark:border-white/10 bg-white dark:bg-white/[0.025]'
                      }`}
                    >
                      <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-md bg-[#8B3FC5]/15 text-[#8B3FC5] border border-[#8B3FC5]/30">
                        <Icon size={16} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="text-sm font-semibold">{d.label}</div>
                          {d.is_current && (
                            <span
                              data-testid={`trusted-device-current-${d.id}`}
                              className="text-[9px] uppercase tracking-[0.22em] font-bold rounded-full px-2 py-0.5 bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                            >
                              This device
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500 dark:text-white/50 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Globe2 size={10} /> {d.ip || 'unknown ip'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={10} /> Last used {relative(d.last_used_at)}
                          </span>
                          {expiresIn !== null && (
                            <span className="flex items-center gap-1">
                              <ExternalLink size={10} /> Expires in {expiresIn}d
                            </span>
                          )}
                        </div>
                        {d.ua && (
                          <div className="mt-2 text-[10px] text-slate-400 dark:text-white/30 truncate max-w-xl" title={d.ua}>
                            {d.ua}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => revoke(d.id)}
                        disabled={busyId === d.id}
                        data-testid={`trusted-device-revoke-${d.id}`}
                        className="ml-auto inline-flex items-center gap-1 rounded-md border border-slate-300 dark:border-white/15 text-slate-700 dark:text-white/70 text-[11px] font-semibold px-2.5 py-1.5 transition-all hover:bg-rose-50 dark:hover:bg-rose-500/10 hover:border-rose-300 dark:hover:border-rose-500/40 hover:text-rose-700 dark:hover:text-rose-300 disabled:opacity-50"
                      >
                        {busyId === d.id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
                        Revoke
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Revoked devices */}
          {revokedDevices.length > 0 && (
            <section className="space-y-3" data-testid="trusted-devices-revoked-list">
              <h2 className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-white/45 font-bold">
                Recently revoked ({revokedDevices.length})
              </h2>
              <ul className="space-y-2 opacity-60">
                {revokedDevices.map((d) => {
                  const Icon = deviceIcon(d.ua);
                  return (
                    <li
                      key={d.id}
                      className="flex items-start gap-3 rounded-lg border border-slate-200 dark:border-white/10 bg-slate-50/60 dark:bg-white/[0.015] p-4"
                    >
                      <span className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-md bg-slate-100 dark:bg-white/10 text-slate-400 border border-slate-200 dark:border-white/10">
                        <Icon size={16} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium line-through">{d.label}</div>
                        <div className="mt-1 text-[11px] text-slate-400 dark:text-white/40">
                          Revoked {relative(d.revoked_at)}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </>
      )}
    </main>
  );
}
